// src/api/appUsers.js
import { apiGet, apiPost, apiPatch, apiDelete } from "lib/apiClient";

// Import getAccessToken for direct fetch calls
function getAccessToken() {
    const session = localStorage.getItem('auth_session');
    if (session) {
        try {
            const data = JSON.parse(session);
            return data.access_token || null;
        } catch (e) {
            return null;
        }
    }
    return null;
}

const API_BASE = process.env.REACT_APP_API_BASE || '/api';


/* =========================
 * Список / чтение
 * ========================= */

export async function listAppUsers({ q = "" } = {}) {
    const params = {};
    if (q) params.q = q;

    const { data, error } = await apiGet("/app-users/", params);
    if (error) throw new Error(error);
    return data || [];
}

export async function getAppUser(id) {
    const { data, error } = await apiGet(`/app-users/${id}/`);
    if (error) throw new Error(error);
    return data;
}

/* =========================
 * Создание / обновление / удаление
 * ========================= */

export async function createAppUser({
    full_name,
    department = null,
    room_no = null,
    phone = null,
    email = null,
    photo = null,
}) {
    if (!full_name || !full_name.trim()) {
        throw new Error("ФИО является обязательным полем.");
    }

    // Всегда используем FormData для единообразия (даже без фото)
    const formData = new FormData();
    formData.append('full_name', full_name.trim());
    // Добавляем только непустые поля
    if (department && department.toString().trim()) {
        formData.append('department', department.toString().trim());
    }
    if (room_no && room_no.toString().trim()) {
        formData.append('room_no', room_no.toString().trim());
    }
    if (phone && phone.toString().trim()) {
        formData.append('phone', phone.toString().trim());
    }
    if (email && email.toString().trim()) {
        formData.append('email', email.toString().trim());
    }
    if (photo) {
        formData.append('photo', photo);
    }

    const { data, error } = await apiPost("/app-users/", formData);
    if (error) throw new Error(error);
    return data;
}

export async function updateAppUser(id, patch = {}) {
    if (!id) throw new Error("Не передан id пользователя");

    const allowed = ["full_name", "department", "room_no", "phone", "email", "photo"];

    // Если есть фото (файл) или нужно удалить фото (null), используем FormData
    if (patch.photo !== undefined) {
        const formData = new FormData();
        for (const k of allowed) {
            if (patch[k] !== undefined) {
                if (k === 'photo') {
                    if (patch[k] === null) {
                        // Для удаления фото отправляем пустую строку или специальный маркер
                        formData.append('photo', '');
                    } else if (patch[k] instanceof File) {
                        formData.append('photo', patch[k]);
                    }
                } else if (patch[k] !== null && patch[k] !== undefined) {
                    formData.append(k, String(patch[k]).trim());
                }
            }
        }

        const { data, error } = await apiPatch(`/app-users/${id}/`, formData);
        if (error) throw new Error(error);
        return data;
    }

    // Иначе обычный JSON
    const payload = {};
    for (const k of allowed) {
        if (patch[k] !== undefined && k !== 'photo') payload[k] = patch[k];
    }

    if (payload.full_name !== undefined) {
        payload.full_name = String(payload.full_name).trim();
        if (!payload.full_name) throw new Error("ФИО не может быть пустым");
    }
    if (payload.department !== undefined && payload.department !== null)
        payload.department = String(payload.department).trim() || null;
    if (payload.room_no !== undefined && payload.room_no !== null)
        payload.room_no = String(payload.room_no).trim() || null;
    if (payload.phone !== undefined && payload.phone !== null)
        payload.phone = String(payload.phone).trim() || null;
    if (payload.email !== undefined && payload.email !== null)
        payload.email = String(payload.email).trim() || null;

    const { data, error } = await apiPatch(`/app-users/${id}/`, payload);
    if (error) throw new Error(error);
    return data;
}

export async function deleteAppUser(id) {
    if (!id) throw new Error("Не передан id пользователя");
    const { error } = await apiDelete(`/app-users/${id}/`);
    if (error) throw new Error(error);
}

/* =========================
 * Привязки / акты (для профиля пользователя)
 * ========================= */

export async function getAppUserAssignments(id) {
    const { data, error } = await apiGet(`/app-users/${id}/assignments/`);
    if (error) throw new Error(error);
    return data || [];
}

export async function getAppUserActs(id, params = {}) {
    const { data, error } = await apiGet(`/app-users/${id}/acts/`, params);
    if (error) throw new Error(error);
    return data || [];
}

/**
 * Download AppUser History PDF
 * @param {number} id - AppUser ID
 * @returns {Promise<void>}
 */
export async function downloadAppUserHistoryPdf(id) {
    const token = getAccessToken();
    const response = await fetch(`${API_BASE}/app-users/${id}/history_pdf/`, {
        method: 'GET',
        headers: {
            'Authorization': `Bearer ${token}`,
        },
    });

    if (!response.ok) {
        throw new Error('Failed to download PDF');
    }

    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `history_${id}_${new Date().toISOString().split('T')[0]}.pdf`;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
}

/**
 * Manage access (set/reset password) for an AppUser
 * @param {number} id - AppUser ID
 * @param {Object} data - { email, password }
 * @returns {Promise<Object>} - {data, error}
 */
export async function manageAccess(id, data) {
    return apiPost(`/app-users/${id}/manage_access/`, data);
}
